import { useProfile } from "@/context/ProfileContext";
import { bySex } from "@/lib/bySex";
import { IMAGES, MONTHS_IT } from "@/lib/api";
import { Ruler, CalendarHeart, Bell, Luggage } from "lucide-react";
import { motion } from "framer-motion";

function daysUntil(due) {
  if (!due) return null;
  const target = new Date(due.year, due.month - 1, due.day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
}

export default function Hero({ onOpenList }) {
  const { profile } = useProfile();
  const size = profile?.recommended_size;
  const days = profile ? daysUntil(profile.due_date) : null;
  const weeks = days != null ? Math.floor(days / 7) : null;

  return (
    <section id="top" className="relative overflow-hidden bg-[#FAF8F5] mb-grain">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="lg:col-span-6"
        >
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#8C7F78]">
            Abbigliamento neonato · 100% cotone
          </span>
          <h1 className="mt-3 sm:mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2D2825]">
            Il corredino perfetto,
            <br /> <span className="text-[#C87D75] italic">{bySex(profile?.sex, { femmina: "dolce come lei", maschio: "dolce come lui", other: "dolce come il tuo bebè" })}</span>
          </h1>
          <p className="mt-3 sm:mt-5 text-sm sm:text-base text-[#5C534E] max-w-md leading-relaxed">
            {profile
              ? `Ciao ${profile.name.split(" ")[0]}, abbiamo preparato consigli su misura per l'arrivo ${bySex(profile.sex, { femmina: "della tua bambina", maschio: "del tuo bambino", other: "del tuo bebè" })} a ${profile.city}.`
              : "Consigli su misura, catalogo 100% cotone e la tua lista nascita da condividere con chi ti vuole bene."}
          </p>

          {profile && (
            <div className="mt-5 sm:mt-8 grid grid-cols-2 sm:flex sm:flex-wrap gap-3 sm:gap-4">
              <div data-testid="size-badge"
                className="rounded-2xl bg-white border border-[#EAE3DC] px-3 py-3 sm:px-5 sm:py-4 shadow-sm">
                <div className="flex items-center gap-1.5 text-[#8C7F78] text-[10px] sm:text-xs uppercase tracking-widest mb-1">
                  <Ruler size={13} /> Taglia
                </div>
                <div className="font-serif text-2xl sm:text-3xl text-[#2D2825]">{size?.label}</div>
                <div className="text-xs sm:text-sm text-[#8C7F78] line-clamp-1">{size?.detail}</div>
              </div>

              <div data-testid="countdown-badge"
                className="rounded-2xl bg-white border border-[#EAE3DC] px-3 py-3 sm:px-5 sm:py-4 shadow-sm">
                <div className="flex items-center gap-1.5 text-[#8C7F78] text-[10px] sm:text-xs uppercase tracking-widest mb-1">
                  <CalendarHeart size={13} /> Al grande giorno
                </div>
                <div className="font-serif text-2xl sm:text-3xl text-[#2D2825]">
                  {days > 0 ? `${days} giorni` : days === 0 ? "Oggi!" : "Benvenuto 🤍"}
                </div>
                <div className="text-xs sm:text-sm text-[#8C7F78] line-clamp-1">
                  {weeks > 0 ? `≈ ${weeks} settimane · ` : ""}
                  {MONTHS_IT[profile.due_date.month - 1]} {profile.due_date.year}
                </div>
              </div>
            </div>
          )}

          {profile && (
            <button data-testid="hero-cta-bag" onClick={() => document.getElementById("valigia")?.scrollIntoView({ behavior: "smooth" })}
              className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#2D2825] hover:bg-[#473f3a] text-white px-7 min-h-[48px] text-sm transition-colors">
              <Luggage size={16} /> Scopri la tua valigia per l'ospedale
            </button>
          )}

          {profile && days != null && days >= 0 && days <= 14 && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              data-testid="reminder-alert"
              className="mt-6 flex items-start gap-3 rounded-2xl bg-[#FDF6E7] border border-[#F2D293] px-4 py-3"
            >
              <Bell className="text-[#C29638] mt-0.5" size={18} />
              <div className="text-sm text-[#7a5f1f]">
                Mancano pochi giorni! Hai la tua lista nascita pronta?{" "}
                <button onClick={onOpenList} data-testid="reminder-open-list"
                  className="underline font-semibold text-[#C29638]">Inviala ora</button>.
              </div>
            </motion.div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
          className="lg:col-span-6 relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden shadow-xl">
            <img src={IMAGES.hero} alt="Mamma e bambina con il corredino Minime" className="w-full h-[240px] sm:h-[380px] lg:h-[460px] object-cover object-[center_32%]" />
          </div>
          <div className="hidden sm:block absolute -bottom-5 -left-3 sm:left-6 rounded-2xl bg-white/90 backdrop-blur border border-[#EAE3DC] px-4 py-3 shadow-lg">
            <div className="text-xs uppercase tracking-widest text-[#8C7F78]">Certificato</div>
            <div className="font-serif text-xl text-[#4E7363]">100% Cotone naturale</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
